<template>
	<v-navigation-drawer v-if="isActive && entity" permanent width="280" mobile-breakpoint="0" color="panel_sub"
		theme="dark" class="sc-edit-panel">
		<!-- Header -->
		<v-list-item class="edit-panel-header">
			<template #prepend>
				<v-icon size="20">mdi-layers-outline</v-icon>
			</template>
			<template #title>
				<span class="text-subtitle-2 text-truncate">{{ entity.name }}</span>
			</template>
		</v-list-item>

		<v-card color="panel_main" class="edit-panel-content">
			<!-- Leave without saving. Execute is the only operation that persists -->
			<div class="edit-panel-footer">
				<v-btn color="primary" variant="outlined" rounded block @click="onBack" class="end-edit-btn">
					<template v-slot:prepend>
						<v-icon>mdi-arrow-left</v-icon>
					</template>
					{{ $t("BACK") }}
				</v-btn>
			</div>

			<!-- Name -->
			<v-card-text class="pa-2">
				<div class="text-caption mb-1">{{ $t("NAME") }}</div>
				<v-text-field v-model="nameInput" density="compact" hide-details variant="outlined" @blur="onNameBlur"
					@keydown.enter="onNameEnter" />
			</v-card-text>

			<!-- Edit section (collapsible when executed) -->
			<div>
				<v-card-text class="py-0">
					<v-btn size="36" variant="text" icon @click="editPanelOpen = !editPanelOpen" class="ml-0">
						<v-icon> mdi-play {{ editPanelOpen ? "mdi-rotate-90" : "" }} </v-icon>
					</v-btn>
					<span class="text-caption">{{ $t("SETTINGS") }}</span>
				</v-card-text>
				<v-expand-transition>
					<div v-show="editPanelOpen">
						<!-- Target -->
						<div class="pa-2 pt-0">
							<div class="text-caption mb-1">{{ $t("TARGET") }}</div>
							<v-select v-model="targetSelection" :items="targetItems" item-title="label" item-value="value"
								density="compact" hide-details variant="outlined" :placeholder="$t('SELECT_TARGET')"
								@update:model-value="onTargetChange" />
						</div>

						<!-- Terrain -->
						<div class="pa-2 pt-0">
							<div class="text-caption mb-1">{{ $t("TERRAIN") }}</div>
							<v-select v-model="terrainSelection" :items="terrainItems" item-title="label" item-value="value"
								density="compact" hide-details variant="outlined" :placeholder="$t('SELECT_TERRAIN')"
								:disabled="!targetSelection || terrainItems.length === 0" />
						</div>

						<!-- Spacing -->
						<div class="pa-2 pt-0">
							<div class="text-caption mb-1">{{ $t("COMPACTION_SURFACE_SPACING") }} ({{ spacingUnitLabel }})</div>
							<v-text-field v-model="ui.spacingInput.value" type="number" :min="0.01" :step="0.01" density="compact"
								hide-details variant="outlined" :suffix="spacingUnitLabel" @blur="onSpacingBlur" />
						</div>

						<!-- Slope shoulder offset -->
						<div class="pa-2 pt-0">
							<div class="text-caption mb-1 d-flex align-center">
								{{ $t("SLOPE_SHOULDER_OFFSET") }} ({{ spacingUnitLabel }})
								<v-tooltip location="top" max-width="240">
									<template #activator="{ props }">
										<v-icon v-bind="props" size="14" class="ml-1">mdi-information-outline</v-icon>
									</template>
									<span>{{ $t("SLOPE_SHOULDER_OFFSET_TOOLTIP") }}</span>
								</v-tooltip>
							</div>
							<v-text-field v-model="ui.slopeShoulderOffsetInput.value" type="number" :min="0" :step="0.01"
								density="compact" hide-details variant="outlined" :suffix="spacingUnitLabel"
								@blur="onSlopeShoulderOffsetBlur" />
						</div>

						<!-- Reference Plane Selection -->
						<div class="pa-2 pt-0">
							<div class="text-caption mb-1">
								{{ $t("REFERENCE_PLANE_SELECTION") }}
								<template v-if="faceSelection.selectedCount > 0">
									({{ faceSelection.selectedCount }} {{ $t("FACES") }})
								</template>
							</div>
							<v-btn variant="flat" :color="isFaceSelectionActive ? 'error' : 'primary'" size="small"
								:disabled="!entity.target" @click="onToggleFaceSelection">
								{{ $t("REFERENCE_PLANE_SELECTION") }}
							</v-btn>
							<div v-if="isFaceSelectionActive" class="mt-2">
								<div class="text-caption mb-1">{{ $t("ANGLE_TOLERANCE") }}</div>
								<v-text-field v-model="toleranceSlider" type="number" :min="0" :max="MAX_ANGLE_TOLERANCE"
									density="compact" hide-details variant="outlined" suffix="°" @update:model-value="onToleranceInput" />
							</div>
						</div>

						<!-- Layer mode (ADR 0006) -->
						<div class="pa-2 pt-0">
							<div class="text-caption mb-1">{{ $t("LAYER_MODE") }}</div>
							<v-radio-group v-model="ui.layerModeInput.value" density="compact" hide-details inline
								@update:model-value="onLayerModeChange">
								<v-radio :label="$t('LAYER_MODE_PARALLEL')" :value="layerModes.PARALLEL" />
								<v-radio :value="layerModes.HORIZONTAL">
									<template #label>
										<span class="d-flex align-center">
											{{ $t("LAYER_MODE_HORIZONTAL") }}
											<v-tooltip location="top" max-width="260">
												<template #activator="{ props }">
													<v-icon v-bind="props" size="14" class="ml-1">mdi-help-circle-outline</v-icon>
												</template>
												<span>{{ $t("LAYER_MODE_HORIZONTAL_TOOLTIP") }}</span>
											</v-tooltip>
										</span>
									</template>
								</v-radio>
							</v-radio-group>
						</div>

						<!-- Execute -->
						<div class="pa-2 pt-0">
							<v-btn variant="flat" color="primary" block :loading="isProcessing" :disabled="!canExecute"
								@click="onExecute">
								{{ $t("EXECUTE_SPREADING_COMPACTION") }}
							</v-btn>
							<div v-if="executionError" class="text-error text-caption mt-1">
								{{ executionError }}
							</div>
						</div>
					</div>
				</v-expand-transition>
			</div>

			<!-- Results -->
			<template v-if="hasResult">
				<v-card-text class="pa-2 pt-2">
					<div class="mb-1">
						<div class="text-caption">{{ $t("OPACITY") }}</div>
						<v-slider v-model="opacitySlider" :min="0" :max="1" :step="0.05" density="compact" hide-details thumb-label
							@update:model-value="onOpacityChange" />
					</div>

					<!-- Legacy record: outline missing, so no compaction CSV until re-executed -->
					<div v-if="compactionOffsets.length > 0 && !hasCompactionOutlineResult"
						class="text-caption text-warning mb-1">
						{{ $t("COMPACTION_CSV_REQUIRES_REEXECUTION") }}
					</div>

					<!-- Compaction results (top, expanded by default) -->
					<div v-if="compactionOffsets.length > 0" class="mb-1">
						<v-card-text class="py-0">
							<v-btn size="36" variant="text" icon @click="compactionOpen = !compactionOpen" class="ml-0">
								<v-icon> mdi-play {{ compactionOpen ? "mdi-rotate-90" : "" }} </v-icon>
							</v-btn>
							<span class="text-caption">{{ $t("COMPACTION") }}</span>
						</v-card-text>
						<v-expand-transition>
							<div v-show="compactionOpen">
								<div v-for="offset in compactionOffsets" :key="`compaction_${offset}`" class="sc-offset-row pl-7">
									<v-checkbox :model-value="spreadingCompaction.getOffsetVisible('compaction', offset)"
										density="compact" hide-details :label="spreadingCompaction.formatOffsetLabel(offset)"
										@update:model-value="(v) => onOffsetVisibleToggle('compaction', offset, !!v)" />
								</div>
							</div>
						</v-expand-transition>
					</div>

					<!-- Spreading results (bottom, collapsed by default) -->
					<div v-if="spreadingOffsets.length > 0" class="mb-1">
						<v-card-text class="py-0">
							<v-btn size="36" variant="text" icon @click="spreadingOpen = !spreadingOpen" class="ml-0">
								<v-icon> mdi-play {{ spreadingOpen ? "mdi-rotate-90" : "" }} </v-icon>
							</v-btn>
							<span class="text-caption">{{ $t("SPREADING") }}</span>
						</v-card-text>
						<v-expand-transition>
							<div v-show="spreadingOpen">
								<div v-for="offset in spreadingOffsets" :key="`spreading_${offset}`" class="sc-offset-row pl-7">
									<v-checkbox :model-value="spreadingCompaction.getOffsetVisible('spreading', offset)" density="compact"
										hide-details :label="spreadingCompaction.formatOffsetLabel(offset)"
										@update:model-value="(v) => onOffsetVisibleToggle('spreading', offset, !!v)" />
								</div>
							</div>
						</v-expand-transition>
					</div>

				</v-card-text>
			</template>
		</v-card>
	</v-navigation-drawer>
</template>

<script setup lang="ts">
import {
	type ISpreadingCompaction,
	type TLayerMode,
	layerModes,
} from "@/store/modules/spreading-compaction-types";
import { computed, ref, watch } from "vue";
import { isImeComposingKeyEvent } from "@/utils/imeComposition";
import {
	DEFAULT_ANGLE_TOLERANCE,
	MAX_ANGLE_TOLERANCE,
} from "./spreading-compaction/spreadingCompactionHelpers";
import { useSpreadingCompaction } from "./spreading-compaction/useSpreadingCompaction";
import { useSpreadingCompactionForm } from "./spreading-compaction/useSpreadingCompactionForm";
import { useSpreadingCompactionUnit } from "./spreading-compaction/useSpreadingCompactionUnit";

const spreadingCompaction = useSpreadingCompaction();
const unitContext = useSpreadingCompactionUnit();
const ui = useSpreadingCompactionForm(unitContext);

/** Settings fields only — the result section's opacity and toggles are not reverted. */
type TSettingsSnapshot = Pick<
	ISpreadingCompaction,
	| "target"
	| "terrain"
	| "compactionSurfaceSpacing"
	| "slopeShoulderOffset"
	| "layerMode"
>;
const settingsSnapshot = ref<TSettingsSnapshot | null>(null);

const nameInput = ref("");
const targetSelection = ref<string | null>(null);
const opacitySlider = ref(0.7);
const toleranceSlider = ref(DEFAULT_ANGLE_TOLERANCE);
const editPanelOpen = ref(true);
const compactionOpen = ref(true);
const spreadingOpen = ref(false);
const spacingUnitLabel = computed(
	() => unitContext.unitConfig.value.displayUnit,
);

const {
	selected: entity,
	isActive,
	isProcessing,
	executionError,
	hasResult,
	faceSelection,
	isFaceSelectionActive,
	targetItems,
	terrainItems,
	isTerrainValid,
	isDraftMode,
	spreadingOffsets,
	compactionOffsets,
	hasCompactionOutlineResult,
} = spreadingCompaction;

/**
 * Store-derived rather than a local ref, so a terrain replaced by a target
 * change cannot leave the display behind. Values absent from the candidates
 * read as null to keep the display and the Execute gate in agreement.
 */
const terrainSelection = computed<string | null>({
	get: () => {
		const terrain = entity.value?.terrain;
		if (!terrain || !isTerrainValid.value) return null;
		return `${terrain.type}:${terrain.id}`;
	},
	set: (value) => spreadingCompaction.setTerrainSelection(value),
});

const canExecute = computed(
	() =>
		!!entity.value?.target &&
		isTerrainValid.value &&
		(faceSelection.selectedCount ?? 0) > 0 &&
		!isProcessing.value,
);

// Watches entity identity, not `isDraftMode`: swapping one draft for another
// (close without executing, then Add again) leaves that derived value at `true`,
// so it never fires and the local state below keeps the previous entry's values.
// Only SET_DRAFT replaces a draft and editing a saved entry mutates the same
// object, so this does not re-sync on every keystroke.
watch(
	() => [
		entity.value,
		// Reopening the same entry must reset the fields to the stored values
		isActive.value,
		// The site unit can resolve after mount; re-format the mm values when it does.
		unitContext.unitConfig.value.distanceUnit,
	],
	() => {
		if (entity.value) {
			nameInput.value = entity.value.name;
			opacitySlider.value = entity.value.targetOpacity;
			editPanelOpen.value = !hasResult.value;
			toleranceSlider.value = DEFAULT_ANGLE_TOLERANCE;
			ui.spacingInput.value = unitContext.formatDisplayInput(
				entity.value.compactionSurfaceSpacing,
			);
			ui.slopeShoulderOffsetInput.value = ui.formatSlopeShoulderOffsetInput(
				entity.value.slopeShoulderOffset,
			);
			ui.layerModeInput.value = entity.value.layerMode;
			targetSelection.value = entity.value.target
				? `${entity.value.target.type}:${entity.value.target.id}`
				: null;
			settingsSnapshot.value = {
				target: entity.value.target,
				terrain: entity.value.terrain,
				compactionSurfaceSpacing: entity.value.compactionSurfaceSpacing,
				slopeShoulderOffset: entity.value.slopeShoulderOffset,
				layerMode: entity.value.layerMode,
			};
		}
	},
	{ immediate: true },
);

/**
 * Execute is the only operation that persists, so leaving the panel is not a
 * commit. An unexecuted draft exists nowhere else, hence the discard.
 */
function onBack() {
	if (isDraftMode.value) {
		// cancelDraft also clears the face selection
		spreadingCompaction.cancelDraft();
	} else {
		if (settingsSnapshot.value) {
			spreadingCompaction.update(settingsSnapshot.value);
		}
		// Closing while in face-selection mode leaves the viewer handler running
		spreadingCompaction.exitFaceSelectionMode();
	}
	spreadingCompaction.setActive(false);
}
/** Blur the name field on Enter, except while the IME is composing. */
function onNameEnter(e: KeyboardEvent) {
	if (isImeComposingKeyEvent(e)) return;
	(e.target as HTMLInputElement | null)?.blur();
}

function onNameBlur() {
	if (!entity.value) return;
	const trimmed = nameInput.value.trim();
	if (!trimmed) {
		nameInput.value = entity.value.name;
		return;
	}
	if (trimmed !== entity.value.name) {
		spreadingCompaction.update({ name: trimmed });
	}
}
function onTargetChange(value: string | null) {
	spreadingCompaction.setTargetSelection(value);
}
function onSpacingBlur() {
	const spacingMm = ui.spacingValue.value;
	if (spacingMm === null) return;
	// Show the value that was actually stored, so the field cannot keep digits
	// finer than the unit's granularity.
	ui.spacingInput.value = unitContext.formatDisplayInput(spacingMm);
	if (!entity.value) return;
	spreadingCompaction.update({ compactionSurfaceSpacing: spacingMm });
}
function onSlopeShoulderOffsetBlur() {
	const offsetMm = ui.slopeShoulderOffsetValue.value;
	if (offsetMm === null) return;
	ui.slopeShoulderOffsetInput.value =
		ui.formatSlopeShoulderOffsetInput(offsetMm);
	if (!entity.value) return;
	spreadingCompaction.update({ slopeShoulderOffset: offsetMm });
}
function onLayerModeChange(value: TLayerMode | null) {
	if (value === null || !entity.value) return;
	spreadingCompaction.update({ layerMode: value });
}
function onToggleFaceSelection() {
	if (isFaceSelectionActive.value) {
		spreadingCompaction.exitFaceSelectionMode();
	} else {
		spreadingCompaction.enterFaceSelectionMode();
	}
}
function onToleranceInput(value: string | number) {
	const v = Number.parseFloat(String(value));
	if (Number.isNaN(v) || v < 0 || v > MAX_ANGLE_TOLERANCE) return;
	spreadingCompaction.updateFaceSelectionTolerance(v);
}
function onExecute() {
	spreadingCompaction.executeSelected();
}
function onOpacityChange(value: number) {
	spreadingCompaction.setTargetOpacity(value);
}
async function onOffsetVisibleToggle(
	type: "spreading" | "compaction",
	offset: number,
	visible: boolean,
) {
	if (type === "compaction") {
		for (const o of compactionOffsets.value) {
			spreadingCompaction.setOffsetVisible({
				type: "compaction",
				offset: o,
				visible: visible ? o >= offset : o > offset,
			});
		}
	} else {
		spreadingCompaction.setOffsetVisible({ type, offset, visible });
	}
	await spreadingCompaction.refreshCrossSectionView();
}
</script>

<style scoped>
.sc-edit-panel {
	z-index: 5;
}

.edit-panel-header {
	min-height: 48px;
}

.edit-panel-content {
	overflow-y: auto;
	max-height: calc(100vh - 120px);
}

.edit-panel-footer {
	padding: 16px;
	background-color: #29292f;
	position: relative;
	z-index: 10;
	box-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
}

.end-edit-btn {
	height: 40px;
	font-size: 14px;
}

.sc-offset-row :deep(.v-input) {
	margin-top: 0;
	margin-bottom: 0;
}

.sc-offset-row :deep(.v-selection-control) {
	min-height: 24px;
}
</style>
